/* eslint-disable no-console */
/** @ts-check */

(function () {
  "use strict";

  /**
   * @typedef {{ cookieDays?: number }} BVAffiliateTrackingConfig
   * @typedef {{ config?: BVAffiliateTrackingConfig }} BVAffiliateTracking
   * @typedef {Window & { BV_AFFILIATE_TRACKING?: BVAffiliateTracking }} WindowWithBV
   */

  /** @type {WindowWithBV} */
  var win = window;

  // -----------------------------
  // Config (default 15 days)
  // -----------------------------
  /** @type {number} */
  var COOKIE_DAYS =
    win.BV_AFFILIATE_TRACKING &&
    win.BV_AFFILIATE_TRACKING.config &&
    typeof win.BV_AFFILIATE_TRACKING.config.cookieDays === "number"
      ? win.BV_AFFILIATE_TRACKING.config.cookieDays
      : 15;

  /** @type {string} */
  var SOURCE = "pepperjam";

  // -----------------------------
  // Helpers
  // -----------------------------

  /**
   * @param {string} name
   * @returns {string}
   */
  function getParam(name) {
    try {
      var params = new URLSearchParams(win.location.search);
      var val = params.get(name);
      return typeof val === "string" ? val : "";
    } catch (e) {
      return "";
    }
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {number} days
   * @returns {void}
   */
  function setCookie(name, value, days) {
    try {
      if (!value) return;

      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);

      win.document.cookie =
        encodeURIComponent(name) +
        "=" +
        encodeURIComponent(value) +
        ";expires=" +
        d.toUTCString() +
        ";path=/;SameSite=Lax";
    } catch (e) {
      // ignore
    }
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  function getCookie(name) {
    try {
      var cookies = win.document.cookie || "";
      var re = new RegExp("(^| )" + encodeURIComponent(name) + "=([^;]+)");
      var match = cookies.match(re);

      if (!match || typeof match[2] !== "string") return "";
      return decodeURIComponent(match[2]);
    } catch (e) {
      return "";
    }
  }

  /**
   * @returns {boolean}
   */
  function shouldRunForPepperjam() {
    var src = (getParam("utm_source") || "").toLowerCase();
    return src === "pepperjam";
  }

  /**
   * @param {Record<string, string>} attrs
   * @returns {Record<string, string>}
   */
  function cleanAttributes(attrs) {
    /** @type {Record<string, string>} */
    var cleaned = {};

    Object.keys(attrs).forEach(function (key) {
      var val = attrs[key];
      if (typeof val === "string" && val.trim() !== "") {
        cleaned[key] = val.trim();
      }
    });

    return cleaned;
  }

  /**
   * @param {Record<string, string>} attributes
   * @returns {void}
   */
  function updateCartAttributes(attributes) {
    try {
      if (!attributes || Object.keys(attributes).length === 0) return;

      /** @type {{ attributes: Record<string,string> }} */
      var payload = { attributes: attributes };

      void win.fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
    } catch (e) {
      // ignore
    }
  }

  // -----------------------------
  // Start
  // -----------------------------
  if (!shouldRunForPepperjam()) return;

  // Based strictly on provided PepperJam sample URL:
  // Affiliate ID = nb_subid (109177)
  // Click ID     = clickId OR nb_clid
  var affiliateId = getParam("nb_subid");
  var clickId = getParam("clickId") || getParam("nb_clid");

  // Optional params from sample URL
  var utmCampaign = getParam("utm_campaign");
  var utmContent = getParam("utm_content");
  var utmMedium = getParam("utm_medium");
  var nbPlatform = getParam("nb_platform");
  var nbClid = getParam("nb_clid");
  var nbCreative = getParam("nb_creative");
  var src = getParam("src");

  // Only set cookieDate if actual affiliate signal exists
  var cookieDate = affiliateId || clickId ? new Date().toISOString() : "";

  // Persist cookies if signal exists
  if (affiliateId || clickId) {
    setCookie("pepperjamAffiliateId", affiliateId, COOKIE_DAYS);
    setCookie("pepperjamClickId", clickId, COOKIE_DAYS);
    setCookie("pepperjamCookieDate", cookieDate, COOKIE_DAYS);
    setCookie("affiliateSource", SOURCE, COOKIE_DAYS);

    // optional
    setCookie("pepperjamUtmCampaign", utmCampaign, COOKIE_DAYS);
    setCookie("pepperjamUtmContent", utmContent, COOKIE_DAYS);
    setCookie("pepperjamUtmMedium", utmMedium, COOKIE_DAYS);
    setCookie("pepperjamNbPlatform", nbPlatform, COOKIE_DAYS);
    setCookie("pepperjamNbClid", nbClid, COOKIE_DAYS);
    setCookie("pepperjamNbCreative", nbCreative, COOKIE_DAYS);
    setCookie("pepperjamSrc", src, COOKIE_DAYS);
  }

  // Build attributes from cookie values
  /** @type {Record<string, string>} */
  var rawAttributes = {
    // required
    pepperjamAffiliateId: getCookie("pepperjamAffiliateId"),
    pepperjamClickId: getCookie("pepperjamClickId"),
    pepperjamCookieDate: getCookie("pepperjamCookieDate"),
    affiliateSource: getCookie("affiliateSource") || SOURCE,

    // optional (only attach if exists)
    pepperjamUtmCampaign: getCookie("pepperjamUtmCampaign"),
    pepperjamUtmContent: getCookie("pepperjamUtmContent"),
    pepperjamUtmMedium: getCookie("pepperjamUtmMedium"),
    pepperjamNbPlatform: getCookie("pepperjamNbPlatform"),
    pepperjamNbClid: getCookie("pepperjamNbClid"),
    pepperjamNbCreative: getCookie("pepperjamNbCreative"),
    pepperjamSrc: getCookie("pepperjamSrc"),
  };

  var finalAttributes = cleanAttributes(rawAttributes);

  // Stop if nothing meaningful
  if (!finalAttributes.pepperjamAffiliateId && !finalAttributes.pepperjamClickId) return;

  updateCartAttributes(finalAttributes);
})();
